import LinkIcon from '@mui/icons-material/Link';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContrastIcon from '@mui/icons-material/Contrast';
import EditIcon from '@mui/icons-material/Edit';
import ErrorIcon from '@mui/icons-material/Error';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import SendIcon from '@mui/icons-material/Send';
import Chip from '@mui/material/Chip';
import type { InvoiceStatus } from 'app-components/flux/transactions/invoicesSelectors';
import type { EstimateStatus } from 'app-components/flux/transactions/transactionsTypes';
import React from 'react';
import { useFormatAmount } from 'app-components/hooks/privacyMode/useFormatAmount';

type Props = {
  value?: InvoiceStatus | EstimateStatus | 'credit-available' | 'COMPLETED' | 'LINKED' | null;
  paidAmount?: number;
};

export default function InvoiceStatusChip({ value, paidAmount }: Props) {
  const formatAmount = useFormatAmount();
  let color: 'error' | 'warning' | 'info' | 'success' | 'inactive' | 'pending' | undefined;
  let Icon: React.ReactElement | undefined;
  let label: string | undefined;
  if (value === 'overdue') {
    color = 'error';
    Icon = <ErrorIcon />;
    label = 'Overdue';
  } else if (value === 'draft' || value === 'PENDING') {
    // Estimate PENDING is equivalent to draft status
    color = 'inactive';
    Icon = <EditIcon />;
    label = 'Draft';
  } else if (value === 'partial') {
    color = 'warning';
    Icon = <ContrastIcon />;
    label = paidAmount ? `${formatAmount(paidAmount, 'USD', '0,0.00')} Paid` : 'Partial';
  } else if (value === 'sent') {
    color = 'info';
    Icon = <SendIcon />;
    label = 'Sent';
  } else if (value === 'paid') {
    color = 'success';
    Icon = <CheckCircleIcon />;
    label = 'Paid';
  } else if (value === 'credit-available') {
    color = 'pending';
    label = 'Credit Available';
    Icon = <MonetizationOnIcon />;
  } else if (value === 'ACCEPTED') {
    color = 'success';
    Icon = <CheckCircleIcon />;
    label = 'Accepted';
  } else if (value === 'DECLINED') {
    color = 'error';
    Icon = <ErrorIcon />;
    label = 'Declined';
  } else if (value === 'CONVERTED') {
    color = 'success';
    Icon = <PublishedWithChangesIcon />;
    label = 'Converted';
  } else if (value === 'EXPIRED') {
    color = 'warning';
    Icon = <ContrastIcon />;
    label = 'Expired';
  } else if (value === 'COMPLETED') {
    color = 'success';
    Icon = <TaskAltIcon sx={{ fontSize: '16px ' }} />;
    label = 'Completed';
  } else if (value === 'LINKED') {
    color = 'success';
    Icon = <LinkIcon sx={{ fontSize: '16px ' }} />;
    label = 'Linked';
  }

  return !label ? null : <Chip color={color} icon={Icon} label={label} size='small' variant='tonal' />;
}
